import {
  placementStandardColumns,
  type BasicInfoForTssCampaign,
} from "@specsheet-common/shared-types";
import { getStandardColumnValueForPlacement } from "./getStandardColumnValueForPlacement";
import {
  aggregateAvailableDurationEntriesForBooking,
  aggregateSlotLengthCellTags,
  formatSlotLengthLikeAvailableDuration,
} from "./slotLengthForPlacement";

/** Matches core-client `UNSET_VALUE.id` for Slot Length — filter persisted option ids use this for “Not Set”. */
export const SLOT_LENGTH_FILTER_NOT_SET_ID = "";

type StandardColumnPlacementArg = Parameters<
  typeof getStandardColumnValueForPlacement
>[0];

export type BookingLikeForComputedStandardFilters = {
  placementInfo: Array<{ placement: StandardColumnPlacementArg }>;
  externalCampaignInfo?: BasicInfoForTssCampaign | null;
};

/**
 * Same semantics as {@link createDurationFilter} `filterFunc` (core-client), using persisted option ids.
 */
export const bookingMatchesSavedDurationFilter = (
  booking: BookingLikeForComputedStandardFilters,
  selectedIds: string[] | undefined
): boolean => {
  if (!selectedIds?.length) {
    return true;
  }

  const external = booking.externalCampaignInfo ?? undefined;
  const displayedValues = new Set(
    aggregateAvailableDurationEntriesForBooking(
      booking.placementInfo.flatMap((pi) =>
        getStandardColumnValueForPlacement(pi.placement, {
          externalCampaignInfo: external,
        })[placementStandardColumns.availableDurationColumn]()
      )
    ).map((d) => String(d.value))
  );

  return selectedIds.some((id) => displayedValues.has(String(id)));
};

const getFormattedSlotLengthOrUnset = (
  placement: StandardColumnPlacementArg,
  externalCampaignInfo: BasicInfoForTssCampaign | undefined
): string | null => {
  const raw = getStandardColumnValueForPlacement(placement, {
    externalCampaignInfo,
  }).slotLengthColumn();
  if (raw === null || raw === undefined || raw === "") {
    return raw;
  }
  return formatSlotLengthLikeAvailableDuration(raw);
};

const isSlotLengthUnsetForFilter = (
  placement: StandardColumnPlacementArg,
  externalCampaignInfo: BasicInfoForTssCampaign | undefined
): boolean => {
  const v = getFormattedSlotLengthOrUnset(placement, externalCampaignInfo);
  return v === null || v === undefined || String(v).trim() === "";
};

/**
 * Same semantics as {@link createSlotLengthFilter} `filterFunc` (core-client), using persisted option ids.
 */
export const bookingMatchesSavedSlotLengthFilter = (
  booking: BookingLikeForComputedStandardFilters,
  selectedIds: string[] | undefined
): boolean => {
  if (!selectedIds?.length) {
    return true;
  }

  const external = booking.externalCampaignInfo ?? undefined;

  const hasUnsetSelected = selectedIds.some(
    (id) => id === SLOT_LENGTH_FILTER_NOT_SET_ID
  );
  if (hasUnsetSelected) {
    const hasUnsetPlacement = booking.placementInfo.some((pi) =>
      isSlotLengthUnsetForFilter(pi.placement, external)
    );
    if (hasUnsetPlacement) {
      return true;
    }
  }

  const displayed = aggregateSlotLengthCellTags(
    booking.placementInfo.map((pi) =>
      getStandardColumnValueForPlacement(pi.placement, {
        externalCampaignInfo: external,
      }).slotLengthColumn()
    )
  );

  return selectedIds.some((selectedId) => {
    if (selectedId === SLOT_LENGTH_FILTER_NOT_SET_ID) {
      return false;
    }
    return displayed.includes(String(selectedId));
  });
};
